import { makeAutoObservable, reaction } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";

import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { CreateEntityStore } from "../../common/store/types";
import { userAttachmentEditionService } from "../services/userAttachmentEditionService";
import {
  UserAttachmentEditionValuesInit,
  getUserAttachmentEditionValues,
  UserAttachmentEditionValuesType,
  UserAttachmentEditionErrorsType,
  userAttachmentEditionDisplayId,
} from "./UserAttachmentEditionCommonStore";
import { UserAttachmentEdition } from "../../../api/graphql";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { actualStatusList } from "../../common/types";
import { addedStatusList } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { apiUploadFile } from "../../../api/apiClient";
import { uploadFailureReason } from "../../../common/utils";
import { GridChangeSet } from "../../common/types";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class UserAttachmentEditionCreateStore implements CreateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;

  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: UserAttachmentEditionErrorsType = {};
  service = userAttachmentEditionService;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda: la capa del formulario (SavingOverlay) lo bloquea.
  isSaving: boolean = false;
  savedEntity: UserAttachmentEdition | null = null;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = UserAttachmentEditionValuesInit();
  nameError: string | undefined;
  fileError: string | undefined;
  notesError: string | undefined;
  private uniqueCounter = 1;
  dummyCRUDRestoredObservable: number = 0;
  private customValidators: CustomValidator<UserAttachmentEditionValuesType>[] =
    [];
  private _ownPendingChanges = false;
  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction: (() => void) | null = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: {
        resultId?: string;
        cancelled?: boolean;
        entity?: any;
      }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();

    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(
          v as CustomValidator<UserAttachmentEditionValuesType>
        );
      }
    });
    makeAutoObservable(this);
    this.setupChangeDetection();
    this.saveOriginalState();
  }

  destroy() {
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(
    validator: CustomValidator<UserAttachmentEditionValuesType>
  ) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  get displayId(): string {
    if (this.entityValues) {
      return userAttachmentEditionDisplayId(
        this.entityValues as any,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.UserAttachmentEditionSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setCRUDRowsFromValues(changes: GridChangeSet, values: { string: any }) {
    if (changes.added) {
      changes.added = [values];
    } else if (changes.changed) {
      changes.changed = [values];
    }
  }

  returnedData(_params: CallbackDataParams): any {}

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "null",
        label: this.rootStore.langStore.groupUserAttachmentEditionLabel,
        errors: this.getCardErrors("null"),
      },
    ];
  }

  getNameValue(): string {
    return this.entityValues.name;
  }

  setNameValue(value: string) {
    this.entityValues.name = value;
  }

  fileRestoreFile(id: string) {
    if (this.entityValues.file.length > 1) {
      alert("No puede haber más de un archivo activo " + id);
      return;
    }
  }

  *fileUploadFile(file: File): any {
    const previousFiles = [...this.entityValues.__file_files];
    const previousName = this.entityValues.file;
    const fileId = `${this.uniqueCounter++}`;
    const newFile = {
      __status__: "added" as const,
      id: fileId,
      name: file.name,
      size: file.size,
      formattedSize: formatBytes(file.size),
      mimetype: file.type,
      ticket: "",
    };
    this.entityValues.__file_files.push(newFile);
    this.entityValues.file = file.name;
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;

    const outcome = yield this.uploadTicket(file);
    if (!outcome.ticket) {
      // La subida no terminó: el servidor no tiene el archivo, así que no
      // puede quedar en la lista. Vuelve lo que había y el campo dice por qué.
      this.entityValues.__file_files = previousFiles;
      this.entityValues.file = previousName;
      this.dummyCRUDRestoredObservable = this.uniqueCounter++;
      if (outcome.cancelled) {
        // Lo canceló el usuario desde el modal de espera: no es una falla.
        this.clearFieldError("file");
      } else {
        this.setFieldError(
          "file",
          this.langStore.UserAttachmentEditionFileLabel,
          this.langStore.uploadFailedMessage(file.name, outcome.reason)
        );
      }
      return;
    }
    this.clearFieldError("file");
    const fileIndex = this.entityValues.__file_files.findIndex(
      (f) => f.id === fileId
    );
    if (fileIndex !== -1) {
      this.entityValues.__file_files[fileIndex].ticket = outcome.ticket;
    }
  }

  fileCommitFile(changes: GridChangeSet) {
    if (changes.deleted) {
      const id = changes.deleted[0];
      let index = 0;
      let toRemove;
      for (let file of this.entityValues.__file_files) {
        if (file.id === id) {
          toRemove = index;
          break;
        }
        index++;
      }
      if (toRemove) {
        this.entityValues.__file_files.splice(toRemove, 1);
      }
    }
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  getNotesValue(): string {
    return this.entityValues.notes;
  }

  setNotesValue(value: string) {
    this.entityValues.notes = value;
  }

  *fileUpload(file: File): any {
    const data = yield apiUploadFile(true, file, this.rootStore.activityStore);
    // Una subida cancelada vuelve sin datos.
    return data?.singleUpload?.ticket;
  }

  *uploadTicket(file: File): any {
    // No tira: una subida que falla es un resultado que el campo tiene que
    // mostrar, no una excepción que se pierde en la consola.
    try {
      const ticket = yield this.fileUpload(file);
      // Sin ticket y sin error es un pedido abortado: el botón Cancelar.
      return ticket ? { ticket } : { cancelled: true };
    } catch (error) {
      return {
        reason: uploadFailureReason(error, this.langStore.uploadConnectionLost),
      };
    }
  }

  setFieldError(field: string, fieldLabel: string, message: string) {
    this.errors = {
      ...this.errors,
      [field]: { message, fieldLabel },
    } as UserAttachmentEditionErrorsType;
  }

  clearFieldError(field: string) {
    if (!(field in this.errors)) return;
    const errors: Record<string, unknown> = { ...this.errors };
    delete errors[field];
    this.errors = errors as UserAttachmentEditionErrorsType;
  }

  *fileDelete(ticket: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    yield api.deleteUploadedFile({ ticket });
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.entityValues = UserAttachmentEditionValuesInit();
    this.cardState = { null: { open: true, errors: [] } };
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(
    values: UserAttachmentEditionValuesType
  ): UserAttachmentEditionErrorsType {
    const errors: UserAttachmentEditionErrorsType = {};
    this.clearErrors();
    values.name = values.name.trim();
    if (values.name === "") {
      errors.name = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.UserAttachmentEditionNameLabel,
      };
    }

    if (
      values.__file_files.filter((f: any) =>
        actualStatusList.includes(f.__status__)
      ).length === 0
    ) {
      errors.file = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.UserAttachmentEditionFileLabel,
      };
    }
    {
      const pendingUpload = values.__file_files.find(
        (f: any) => addedStatusList.includes(f.__status__) && !f.ticket
      );
      if (pendingUpload) {
        errors.file = {
          message: this.langStore.uploadNotFinishedMessage(pendingUpload.name),
          fieldLabel: this.langStore.UserAttachmentEditionFileLabel,
        };
      }
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      undefined,
      "UserAttachmentEditionCreateStore"
    );
  }

  *duplicatesUserAttachmentEdition(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    const userAttachmentEditionInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (userAttachmentEditionInstance) {
      this.entityValues = getUserAttachmentEditionValues(
        userAttachmentEditionInstance,
        this.langStore,
        true
      );
    }
  }

  close(returnData?: { resultId?: string; cancelled?: boolean; entity?: any }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else if (returnData?.resultId && !this.rootStore.router.canGoBack()) {
      // Sin página anterior (una pestaña nueva), volver atrás no navega: el
      // formulario quedaría abierto, y un segundo Guardar daría de alta otro
      // registro. Pasa a la vista del registro creado.
      this.rootStore.router.replace(
        `/userAttachmentEdition/view/${returnData.resultId}`
      );
    } else {
      this.rootStore.router.goBack();
    }
  }

  *saveNewUserAttachmentEdition(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: daría de alta
    // el registro dos veces.
    if (this.isProcessing) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que cierre como si éste hubiera
      // salido bien.
      this.savedId = null;
      // Mientras manda, el formulario está bloqueado por su capa.
      this.isSaving = true;
      try {
        this.status = "processing";
        yield this._save();
        this.status = "ok";
      } catch (error) {
        this.status = "error";

        if (error instanceof Error) {
          const apiError = getApiError(error, this.langStore);
          this.showErrorDialog(apiError.code, apiError.title, apiError.message);
        } else {
          this.showErrorDialog(this.langStore.serverError, undefined);
        }
      } finally {
        this.isSaving = false;
      }

      if (this.savedId) {
        this.saveOriginalState();
        this.close({
          resultId: this.savedId,
          entity: { id: this.savedId, displayId: this.displayId },
          cancelled: false,
        });
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  cancelNewUserAttachmentEdition(): void {
    this.cancelled = true;

    // Navigation handled by component

    this.close({ cancelled: true });
  }

  get pendingChanges(): boolean {
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      name: this.entityValues.name,
      notes: this.entityValues.notes,
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  getState(): InitializeParams {
    return {
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getUserAttachmentEditionValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = UserAttachmentEditionValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
  }

  *_save(): any {
    // Subentidad sin alta standalone: se persiste por el create/update anidado del parent.
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const duplicateFromId = params?.duplicateFromId;

      // Si se proporciona un ID para duplicar (desde router.params)
      if (duplicateFromId) {
        yield this.duplicatesUserAttachmentEdition(duplicateFromId);
      }

      // Si se proporciona initialDisplayId (desde "Añadir 'texto'" en autocomplete)
      const initialDisplayId = params?.initialDisplayId;
      if (initialDisplayId) {
        this.entityValues.name = initialDisplayId;
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
